import React, { useState, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Chip,
  Autocomplete,
  CircularProgress,
  Checkbox,
  Divider,
  Alert,
  Stack,
  Tooltip,
  IconButton,
  Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';
import {
  DirectionsCar,
  Folder,
  CheckCircle,
  CloudUpload,
  AccessTime,
  Info,
  ShoppingCartOutlined,
} from '@mui/icons-material';
import { useTranslation } from '@/hooks/useTranslation';
import config from '@/context/config';

interface CarBrand {
  id: string;
  name: string;
  logo: string;
  image: string;
}

interface SolutionOption {
  id: string;
  name: string;
  price: number;
  priceMin?: number;
  priceMax?: number;
  externalId?: string;
  level?: number;
  isFreeWhenStageSelected?: boolean;
  category?: string;
  alias?: string;
}

interface SolutionCategory {
  title: string;
  items: SolutionOption[];
}

interface CarModel {
  id: string;
  name: string;
  year: string;
  years: string[];
  engine: {
    id: string;
    name: string;
    gearbox: string;
    fuel: string;
  };
}

interface VehicleType {
  id: string;
  name: string;
}

interface EcuOption {
  id: string;
  code?: string;
  name?: string;
}

interface ManualVehicleData {
  brand: string;
  model: string;
  year: string;
  engine: string;
  gearbox: string;
  fuel: string;
  ecuBrand: string;
  ecuModel: string;
  vinCode: string;
  power: string;
}

const YEAR_OPTIONS: string[] = [];
for (let year = 2026; year >= 1997; year--) {
  YEAR_OPTIONS.push(String(year));
}

const READING_MODE_OPTIONS = ['OBD', 'Bench', 'Boot'];

const ECU_BRAND_OPTIONS = [
  'Audi Multitronic',
  'Bosch',
  'Campi',
  'Caterpillar',
  'Chrysler',
  'Delco',
  'Delphi',
  'Denso',
  'EFI',
  'GMPT',
  'Hella',
  'Hitachi',
  'Kefico',
  'Keihin',
  'LUCAS',
  'Marelli',
  'Mitsubishi',
  'Motiv',
  'Motorola / Cummins',
  'Phoenix',
  'SISU',
  'Saab',
  'Sagem',
  'Schindenden',
  'Siemens / Continental',
  'TRW',
  'Temic',
  'Transtron',
  'VDO',
  'Valeo',
  'Visteon',
  'ZF',
];

const extractPowerFromEngine = (engineName?: string | null) => {
  if (!engineName) return '';
  const match = engineName.match(/(\d+\s*HP)/i);
  return match ? match[1].toUpperCase() : '';
};

interface FileServiceForm {
  makeType: string;
  car: string;
  controller: string;
  model: string;
  engine: string;
  vinCode: string;
  ecuBrand: string;
  ecuModel: string;
  year: string;
  gearbox: string;
  fuelType: string;
  registrationVin: string;
  exactlyTypeOfECU: string;
}

const NewFileServicePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FileServiceForm>({
    makeType: '',
    car: '',
    controller: '',
    model: '',
    engine: '',
    vinCode: '',
    ecuBrand: '',
    ecuModel: '',
    year: '',
    gearbox: 'Automatic',
    fuelType: 'Petrol',
    registrationVin: '',
    exactlyTypeOfECU: '',
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [carBrands, setCarBrands] = useState<CarBrand[]>([]);
  const [carSearchInput, setCarSearchInput] = useState<string>('');
  const [manualCarBrandInput, setManualCarBrandInput] = useState<string>('');
  const [selectedManualCarBrand, setSelectedManualCarBrand] =
    useState<CarBrand | null>(null);
  const [loadingCarBrands, setLoadingCarBrands] = useState<boolean>(false);
  const [selectedCarBrand, setSelectedCarBrand] = useState<CarBrand | null>(
    null
  );

  // Vehicle types states
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loadingVehicleTypes, setLoadingVehicleTypes] =
    useState<boolean>(false);

  // Model states
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [modelSearchInput, setModelSearchInput] = useState<string>('');
  const [loadingCarModels, setLoadingCarModels] = useState<boolean>(false);
  const [selectedCarModel, setSelectedCarModel] = useState<CarModel | null>(
    null
  );

  // Year states
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [yearSearchInput, setYearSearchInput] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  // Engine states
  const [availableEngines, setAvailableEngines] = useState<
    CarModel['engine'][]
  >([]);
  const [engineSearchInput, setEngineSearchInput] = useState<string>('');
  const [selectedEngine, setSelectedEngine] = useState<
    CarModel['engine'] | null
  >(null);

  // ECU selection states
  const [ecuBrands, setEcuBrands] = useState<EcuOption[]>([]);
  const [ecuBrandSearchInput, setEcuBrandSearchInput] = useState<string>('');
  const [loadingEcuBrands, setLoadingEcuBrands] = useState<boolean>(false);
  const [selectedEcuBrand, setSelectedEcuBrand] = useState<EcuOption | null>(
    null
  );

  const [ecuModels, setEcuModels] = useState<EcuOption[]>([]);
  const [ecuModelSearchInput, setEcuModelSearchInput] = useState<string>('');
  const [loadingEcuModels, setLoadingEcuModels] = useState<boolean>(false);
  const [selectedEcuModel, setSelectedEcuModel] = useState<EcuOption | null>(
    null
  );

  const [vehicleEntryMode, _setVehicleEntryMode] = useState<'list' | 'manual'>(
    'manual'
  );
  const [manualVehicleData, setManualVehicleData] = useState<ManualVehicleData>(
    {
      brand: '',
      model: '',
      year: '',
      engine: '',
      gearbox: '',
      fuel: '',
      ecuBrand: '',
      ecuModel: '',
      vinCode: '',
      power: '',
    }
  );

  const handleManualVehicleChange =
    (field: keyof ManualVehicleData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setManualVehicleData(prev => ({
        ...prev,
        [field]: value,
      }));
    };

  // const handleVehicleEntryModeChange = (
  //   _event: React.SyntheticEvent,
  //   newValue: 'list' | 'manual',
  // ) => {
  //   setVehicleEntryMode(newValue);
  //   setFormError(null);
  // };

  const [showSolutionsStep, setShowSolutionsStep] = useState(false);
  const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);
  const [description, setDescription] = useState<string>('');
  const [readingMode, setReadingMode] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [solutionCatalog, setSolutionCatalog] = useState<SolutionCategory[]>(
    []
  );
  const [loadingSolutions, setLoadingSolutions] = useState<boolean>(false);
  const [creatingFileService, setCreatingFileService] = useState(false);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [calculatingPrice, setCalculatingPrice] = useState<boolean>(false);
  const [processingCount, setProcessingCount] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Filter only .mmf, .mmf2 and .bin files
      const validFiles = acceptedFiles.filter(file => {
        const fileName = file.name.toLowerCase();
        return (
          fileName.endsWith('.mmf') ||
          fileName.endsWith('.mmf2') ||
          fileName.endsWith('.bin')
        );
      });
      setUploadedFiles(prev => [...prev, ...validFiles]);

      // Show error if any files were rejected
      if (
        rejectedFiles.length > 0 ||
        acceptedFiles.length !== validFiles.length
      ) {
        // You can add a snackbar notification here if needed
        console.warn('Only .mmf, .mmf2 and .bin files are allowed');
      }
    },
    multiple: true,
    accept: {
      'application/octet-stream': ['.mmf', '.mmf2', '.bin'],
    },
  });

  // Load vehicle types on mount
  useEffect(() => {
    const loadVehicleTypes = async () => {
      setLoadingVehicleTypes(true);
      try {
        const response = await apiService.getVehicleTypes();
        setVehicleTypes(response.items);
        // Set default value if available and formData.makeType is empty
        if (response.items.length > 0) {
          setFormData(prev => {
            if (!prev.makeType) {
              return {
                ...prev,
                makeType: response.items[0].id,
              };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Error loading vehicle types:', error);
        setVehicleTypes([]);
      } finally {
        setLoadingVehicleTypes(false);
      }
    };

    loadVehicleTypes();
  }, []);

  // Debounced search for car brands
  useEffect(() => {
    const searchCarBrands = async () => {
      setLoadingCarBrands(true);
      try {
        // If search is empty, get first 10 records. Otherwise, search with query
        const searchQuery = carSearchInput.trim() || undefined;
        const response = await apiService.getCarBrands(searchQuery);
        setCarBrands(response.items);
      } catch (error) {
        console.error('Error loading car brands:', error);
        setCarBrands([]);
      } finally {
        setLoadingCarBrands(false);
      }
    };

    const timeoutId = setTimeout(searchCarBrands, 300);
    return () => clearTimeout(timeoutId);
  }, [carSearchInput]);

  // Reset model, year, engine when brand changes
  useEffect(() => {
    if (!selectedCarBrand) {
      setSelectedCarModel(null);
      setSelectedYear(null);
      setSelectedEngine(null);
      setCarModels([]);
      setAvailableYears([]);
      setAvailableEngines([]);
      setFormData(prev => ({
        ...prev,
        car: '',
        model: '',
        year: '',
        engine: '',
      }));
    }
  }, [selectedCarBrand]);

  // Debounced search for car models
  useEffect(() => {
    if (!selectedCarBrand) {
      setCarModels([]);
      return;
    }

    const searchCarModels = async () => {
      setLoadingCarModels(true);
      try {
        const searchQuery = modelSearchInput.trim() || undefined;
        const response = await apiService.getCarModels({
          brand_id: selectedCarBrand.id,
          search: searchQuery,
        });
        setCarModels(response.items);
      } catch (error) {
        console.error('Error loading car models:', error);
        setCarModels([]);
      } finally {
        setLoadingCarModels(false);
      }
    };

    const timeoutId = setTimeout(searchCarModels, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedCarBrand, modelSearchInput]);

  // Reset year and engine when model changes
  useEffect(() => {
    if (!selectedCarModel) {
      setAvailableYears([]);
      setSelectedYear(null);
      setSelectedEngine(null);
      setAvailableEngines([]);
      setFormData(prev => ({
        ...prev,
        model: '',
        year: '',
        engine: '',
      }));
    } else {
      // Update available years from selected model
      const uniqueYears = [...new Set(selectedCarModel.years)].sort((a, b) =>
        b.localeCompare(a)
      );
      setAvailableYears(uniqueYears);
      // Reset year and engine when model changes
      setSelectedYear(null);
      setSelectedEngine(null);
      setAvailableEngines([]);
      setFormData(prev => ({
        ...prev,
        year: '',
        engine: '',
      }));
    }
  }, [selectedCarModel]);

  // Reset engine when year changes
  useEffect(() => {
    if (!selectedYear) {
      setSelectedEngine(null);
      setAvailableEngines([]);
      setFormData(prev => ({
        ...prev,
        engine: '',
      }));
    }
  }, [selectedYear]);

  // Load engines when model and year are selected
  useEffect(() => {
    if (!selectedCarModel || !selectedYear || !selectedCarBrand) {
      setAvailableEngines([]);
      return;
    }

    const loadEngines = async () => {
      try {
        const response = await apiService.getCarModels({
          brand_id: selectedCarBrand.id,
          model_id: selectedCarModel.id,
          year: selectedYear,
        });

        // Extract unique engines from results
        const engines = response.items
          .map(item => item.engine)
          .filter(
            (engine, index, self) =>
              index === self.findIndex(e => e.id === engine.id)
          );
        setAvailableEngines(engines);
      } catch (error) {
        console.error('Error loading engines:', error);
        setAvailableEngines([]);
      }
    };

    loadEngines();
  }, [selectedCarModel, selectedYear, selectedCarBrand]);

  // Reset ECU selections when engine changes
  useEffect(() => {
    if (!selectedEngine) {
      setSelectedEcuBrand(null);
      setSelectedEcuModel(null);
      setEcuBrands([]);
      setEcuModels([]);
      setFormData(prev => ({
        ...prev,
        ecuBrand: '',
        ecuModel: '',
      }));
    }
  }, [selectedEngine]);

  // Load ECU brands when engine is selected and search input changes
  useEffect(() => {
    if (!selectedEngine) {
      return;
    }

    const searchEcuBrands = async () => {
      setLoadingEcuBrands(true);
      try {
        const searchQuery = ecuBrandSearchInput.trim() || undefined;
        const response = await apiService.getEcuBrands(searchQuery);
        setEcuBrands(response.items);
      } catch (error) {
        console.error('Error loading ECU brands:', error);
        setEcuBrands([]);
      } finally {
        setLoadingEcuBrands(false);
      }
    };

    const timeoutId = setTimeout(searchEcuBrands, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedEngine, ecuBrandSearchInput]);

  // Reset ECU models when ECU brand changes
  useEffect(() => {
    setSelectedEcuModel(null);
    setEcuModels([]);
  }, [selectedEcuBrand]);

  // Load ECU models when brand selected
  useEffect(() => {
    if (!selectedEngine || !selectedEcuBrand) {
      setEcuModels([]);
      return;
    }

    const searchEcuModels = async () => {
      setLoadingEcuModels(true);
      try {
        const searchQuery = ecuModelSearchInput.trim() || undefined;
        const response = await apiService.getEcuModels({
          brand_id: selectedEcuBrand.id,
          search: searchQuery,
        });
        setEcuModels(response.items);
      } catch (error) {
        console.error('Error loading ECU models:', error);
        setEcuModels([]);
      } finally {
        setLoadingEcuModels(false);
      }
    };

    const timeoutId = setTimeout(searchEcuModels, 300);
    return () => clearTimeout(timeoutId);
  }, [selectedEngine, selectedEcuBrand, ecuModelSearchInput]);

  // Load solutions and prices when solutions step is shown
  useEffect(() => {
    if (!showSolutionsStep) return;

    const loadSolutions = async () => {
      setLoadingSolutions(true);
      try {
        const solutionsResponse = await apiService.getSolutions();

        const solutions = solutionsResponse.items;

        // Filter solutions by selected vehicle type
        const filteredSolutions = formData.makeType
          ? solutions.filter(
              solution => solution.vehicleTypeId === formData.makeType
            )
          : solutions;

        const mapSolutionToOption = (
          solution: (typeof filteredSolutions)[number]
        ) => ({
          id: solution.externalId,
          externalId: solution.externalId,
          name: solution.name,
          price: 0,
          level: solution.level,
          isFreeWhenStageSelected: solution.isFreeWhenStageSelected,
          category: solution.category,
          alias: solution.alias,
        });

        // Group solutions by category
        const solutionsByCategory = new Map<string, typeof filteredSolutions>();

        filteredSolutions.forEach(solution => {
          const categoryName = solution.category || 'Uncategorized';
          if (!solutionsByCategory.has(categoryName)) {
            solutionsByCategory.set(categoryName, []);
          }
          solutionsByCategory.get(categoryName)!.push(solution);
        });

        // Hardcoded category order: Tuning, Free, Deactivations, Individual
        const categoryOrder = ['Tuning', 'Free', 'Deactivations', 'Individual'];

        // Helper function to get category index (case-insensitive)
        const getCategoryIndex = (categoryName: string): number => {
          const normalizedName = categoryName.trim();
          return categoryOrder.findIndex(
            orderName =>
              orderName.toLowerCase() === normalizedName.toLowerCase()
          );
        };

        // Convert to catalog sections
        const catalog: SolutionCategory[] = Array.from(
          solutionsByCategory.entries()
        )
          .map(([categoryName, categorySolutions]) => ({
            title: categoryName,
            items: categorySolutions.map(mapSolutionToOption),
          }))
          .filter(section => section.items.length > 0)
          .sort((a, b) => {
            const indexA = getCategoryIndex(a.title);
            const indexB = getCategoryIndex(b.title);

            // If both are in the order list, sort by their position
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            // If only A is in the order list, A comes first
            if (indexA !== -1) {
              return -1;
            }
            // If only B is in the order list, B comes first
            if (indexB !== -1) {
              return 1;
            }
            // If neither is in the order list, sort alphabetically
            return a.title.localeCompare(b.title);
          });

        setSolutionCatalog(catalog);
      } catch (error) {
        console.error('Error loading solutions:', error);
        setFormError(t('newFileService.errors.failedToLoadSolutions'));
      } finally {
        setLoadingSolutions(false);
      }
    };

    loadSolutions();
  }, [showSolutionsStep, formData.makeType]);

  const isSelectModeReady =
    vehicleEntryMode === 'list' &&
    Boolean(
      selectedCarBrand &&
      selectedCarModel &&
      selectedYear &&
      selectedEngine &&
      selectedEcuBrand &&
      selectedEcuModel &&
      formData.vinCode.trim() &&
      readingMode.trim()
    );

  const isManualModeReady =
    vehicleEntryMode === 'manual' &&
    Boolean(
      manualVehicleData.brand.trim() &&
      manualVehicleData.model.trim() &&
      manualVehicleData.year.trim() &&
      manualVehicleData.engine.trim() &&
      manualVehicleData.vinCode.trim() &&
      manualVehicleData.ecuBrand.trim() &&
      manualVehicleData.ecuModel.trim() &&
      readingMode.trim()
    );
  const autoPowerLabel = selectedEngine
    ? extractPowerFromEngine(selectedEngine.name)
    : '';

  const vehicleSummaryCard = isSelectModeReady ? (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.default',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        {selectedCarBrand!.logo && (
          <Box
            component="img"
            src={selectedCarBrand!.logo}
            alt={`${selectedCarBrand!.name} logo`}
            sx={{
              width: 56,
              height: 56,
              borderRadius: '10px',
              objectFit: 'contain',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              p: 0.75,
            }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="body1" fontWeight={600}>
            {selectedCarBrand!.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.model')}{' '}
            {selectedCarModel!.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.year')}{' '}
            {selectedYear!}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.engine')}{' '}
            {selectedEngine!.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.gearbox')}{' '}
            {selectedEngine!.gearbox}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.fuel')}{' '}
            {selectedEngine!.fuel}
          </Typography>
          {autoPowerLabel && (
            <Typography variant="body2" color="text.secondary">
              {t('newFileService.vehicleInformation.summary.power')}{' '}
              {autoPowerLabel}
            </Typography>
          )}
          {formData.vinCode && (
            <Typography variant="body2" color="text.secondary">
              {t('newFileService.vehicleInformation.summary.vin')}{' '}
              {formData.vinCode}
            </Typography>
          )}
          {selectedEcuBrand && (
            <Typography variant="body2" color="text.secondary">
              {t('newFileService.vehicleInformation.summary.ecuBrand')}{' '}
              {selectedEcuBrand?.name}
            </Typography>
          )}
          {selectedEcuModel && (
            <Typography variant="body2" color="text.secondary">
              {t('newFileService.vehicleInformation.summary.ecuModel')}{' '}
              {selectedEcuModel?.name}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  ) : isManualModeReady ? (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.default',
      }}
    >
      <Stack spacing={0.5}>
        <Typography variant="body1" fontWeight={600}>
          {manualVehicleData.brand} · {manualVehicleData.model}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('newFileService.vehicleInformation.summary.year')}{' '}
          {manualVehicleData.year || '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('newFileService.vehicleInformation.summary.engine')}{' '}
          {manualVehicleData.engine || '—'}
        </Typography>
        {manualVehicleData.gearbox && (
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.gearbox')}{' '}
            {manualVehicleData.gearbox}
          </Typography>
        )}
        {manualVehicleData.fuel && (
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.fuel')}{' '}
            {manualVehicleData.fuel}
          </Typography>
        )}
        {manualVehicleData.ecuBrand && (
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.ecuBrand')}{' '}
            {manualVehicleData.ecuBrand}
          </Typography>
        )}
        {manualVehicleData.ecuModel && (
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.ecuModel')}{' '}
            {manualVehicleData.ecuModel}
          </Typography>
        )}
        {manualVehicleData.power && (
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.power')}{' '}
            {manualVehicleData.power}
          </Typography>
        )}
        {manualVehicleData.vinCode && (
          <Typography variant="body2" color="text.secondary">
            {t('newFileService.vehicleInformation.summary.vin')}{' '}
            {manualVehicleData.vinCode}
          </Typography>
        )}
      </Stack>
    </Paper>
  ) : null;

  const handleProceedToSolutions = () => {
    if (vehicleEntryMode === 'list') {
      if (
        !selectedCarBrand ||
        !selectedCarModel ||
        !selectedYear ||
        !selectedEngine ||
        !selectedEcuBrand ||
        !selectedEcuModel
      ) {
        setFormError(t('newFileService.errors.completeVehicleDetails'));
        return;
      }
      if (!formData.vinCode.trim()) {
        setFormError(t('newFileService.errors.vinCodeRequired'));
        return;
      }
    } else {
      if (!isManualModeReady) {
        setFormError(t('newFileService.errors.fillRequiredFields'));
        return;
      }
      if (!manualVehicleData.vinCode.trim()) {
        setFormError(t('newFileService.errors.vinCodeRequired'));
        return;
      }
    }

    if (!readingMode.trim()) {
      setFormError(t('newFileService.errors.fillRequiredFields'));
      return;
    }

    if (uploadedFiles.length === 0) {
      setFormError(t('newFileService.errors.uploadFile'));
      return;
    }

    setFormError(null);
    setShowSolutionsStep(true);
  };

  const handleSolutionToggle =
    (solutionId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedSolutions(prev =>
        event.target.checked
          ? [...prev, solutionId]
          : prev.filter(id => id !== solutionId)
      );
    };

  const buildEcuLabel = (brand?: string | null, model?: string | null) => {
    return [brand, model].filter(Boolean).join(' ').trim();
  };

  const handleCreateFileService = async () => {
    if (!readingMode.trim()) {
      setFormError(t('newFileService.errors.fillRequiredFields'));
      return;
    }

    const canCreate =
      (vehicleEntryMode === 'list' && isSelectModeReady) ||
      (vehicleEntryMode === 'manual' && isManualModeReady);

    if (!canCreate) {
      return;
    }

    if (selectedSolutions.length === 0) {
      setFormError(t('newFileService.errors.selectSolution'));
      return;
    }

    if (uploadedFiles.length === 0) {
      setFormError(t('newFileService.errors.uploadFile'));
      setShowSolutionsStep(false);
      return;
    }

    const vehicleTypeId = formData.makeType || vehicleTypes[0]?.id || '';
    if (!vehicleTypeId) {
      setFormError(t('newFileService.errors.vehicleTypeRequired'));
      return;
    }

    let carBrandId = '';
    let carModelValue = '';
    let carEngineValue = '';
    let carYearValue = '';
    let carGearboxValue = '';
    let carFuelTypeValue = '';
    let carPowerValue = '';
    let ecuLabel = '';
    let vinValue = '';

    if (vehicleEntryMode === 'list') {
      carBrandId = selectedCarBrand!.id;
      carModelValue = selectedCarModel!.name;
      carEngineValue = selectedEngine!.name;
      carYearValue = selectedYear!;
      carGearboxValue = selectedEngine!.gearbox;
      carFuelTypeValue = selectedEngine!.fuel;
      const powerMatch = extractPowerFromEngine(selectedEngine!.name);
      carPowerValue = powerMatch || selectedEngine!.name || 'N/A';
      ecuLabel = buildEcuLabel(selectedEcuBrand?.name, selectedEcuModel?.name);
      vinValue = formData.vinCode;
    } else {
      carBrandId = manualVehicleData.brand;
      carModelValue = manualVehicleData.model;
      carEngineValue = manualVehicleData.engine;
      carYearValue = manualVehicleData.year;
      carGearboxValue = manualVehicleData.gearbox || 'N/A';
      carFuelTypeValue = manualVehicleData.fuel || 'N/A';
      carPowerValue =
        manualVehicleData.power || manualVehicleData.engine || 'N/A';
      ecuLabel = buildEcuLabel(
        manualVehicleData.ecuBrand,
        manualVehicleData.ecuModel
      );
      vinValue = manualVehicleData.vinCode;
    }

    if (!carBrandId || !carModelValue || !carEngineValue || !carYearValue) {
      setFormError(t('newFileService.errors.completeDetails'));
      return;
    }

    if (!vinValue.trim()) {
      setFormError(t('newFileService.errors.vinCodeRequired'));
      return;
    }

    const requestPayload = {
      vehicleTypeId,
      carBrandId,
      carModel: carModelValue,
      carEngine: carEngineValue,
      carPower: carPowerValue || 'N/A',
      carYear: carYearValue,
      carGearbox: carGearboxValue || 'N/A',
      carFuelType: carFuelTypeValue || 'N/A',
      ecu: ecuLabel || 'N/A',
      vinCode: vinValue,
      readingMode,
      solutions: selectedSolutions,
      originalFile: uploadedFiles[0],
    };

    setCreatingFileService(true);
    setFormError(null);

    try {
      // Check balance before creating file service
      const balanceResponse = await apiService.getBalance();
      const userBalance = balanceResponse.balance;

      if (userBalance < totalPrice) {
        setSnackbarMessage(
          t('newFileService.errors.insufficientCredits', {
            required: totalPrice,
            available: userBalance,
          })
        );
        setSnackbarOpen(true);
        setCreatingFileService(false);
        return;
      }

      const createdFile = await apiService.createFileService(requestPayload);
      if (createdFile?.id) {
        // If description is not empty, send it as a message
        if (description.trim()) {
          try {
            await apiService.sendFileMessage(createdFile.id, {
              message: description.trim(),
            });
          } catch (messageError: any) {
            console.error('Error sending description message:', messageError);
            // Continue with navigation even if message sending fails
          }
        }
        navigate(`/file-service/${createdFile.id}`);
      } else {
        navigate('/file-service');
      }
    } catch (error: any) {
      setFormError(error?.message || t('newFileService.errors.failedToCreate'));
    } finally {
      setCreatingFileService(false);
    }
  };

  const handleFileRemove = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const loadQueueInfo = async () => {
      try {
        const response = await apiService.getFilesQueue();
        setProcessingCount(response.processingCount ?? 0);
      } catch (error) {
        console.error('Error loading files queue:', error);
      }
    };

    loadQueueInfo();
  }, []);

  useEffect(() => {
    if (vehicleEntryMode === 'manual') {
      const matchedBrand = carBrands.find(
        brand => brand.id === manualVehicleData.brand
      );
      if (matchedBrand) {
        setSelectedManualCarBrand(matchedBrand);
        setManualCarBrandInput(matchedBrand.name);
      }
    }
  }, [vehicleEntryMode, manualVehicleData.brand, carBrands]);

  // --- Pricing helpers for solutions ---
  const flatSolutions = useMemo(
    () => solutionCatalog.flatMap(section => section.items),
    [solutionCatalog]
  );
  const selectedSolutionItems = useMemo(
    () => flatSolutions.filter(item => selectedSolutions.includes(item.id)),
    [flatSolutions, selectedSolutions]
  );

  const hasLevel1Selected = useMemo(
    () => selectedSolutionItems.some(item => item.level === 1),
    [selectedSolutionItems]
  );

  // Remove free solutions (level 2) if Stage (level 1) is not selected
  useEffect(() => {
    if (
      !hasLevel1Selected &&
      selectedSolutions.length > 0 &&
      solutionCatalog.length > 0
    ) {
      // Flatten solutions from catalog
      const allSolutions = solutionCatalog.flatMap(section => section.items);

      // Find all level 2 solutions that are free when stage is selected
      const freeSolutionIds = allSolutions
        .filter(item => item.level === 2 && item.isFreeWhenStageSelected)
        .map(item => item.id);

      // Only update if there are actually free solutions to remove
      if (freeSolutionIds.length > 0) {
        setSelectedSolutions(prev => {
          const filtered = prev.filter(id => !freeSolutionIds.includes(id));
          // Only update if something actually changed
          if (filtered.length !== prev.length) {
            return filtered;
          }
          return prev;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLevel1Selected, solutionCatalog]);

  // Calculate price when selected solutions or vehicle type changes
  useEffect(() => {
    if (selectedSolutions.length > 0 && formData.makeType) {
      const calculatePrice = async () => {
        try {
          setCalculatingPrice(true);
          const response = await apiService.calculatePricing({
            solutions: selectedSolutions,
            vehicleTypeId: formData.makeType,
          });
          setTotalPrice(response.creditAmount);
        } catch (err: any) {
          console.error('Error calculating price:', err);
          setTotalPrice(0);
        } finally {
          setCalculatingPrice(false);
        }
      };
      calculatePrice();
    } else {
      setTotalPrice(0);
    }
  }, [selectedSolutions, formData.makeType]);
  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DirectionsCar sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {t('newFileService.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('newFileService.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Service Status */}
        <Paper
          sx={{
            p: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime sx={{ color: 'success.main' }} />
              <Typography variant="body2">
                <strong>{t('newFileService.currentQueue')}</strong> #
                {processingCount} {t('newFileService.files')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t('newFileService.estimatedDeliveryTime')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('newFileService.automaticProcessing')}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Main Form Area */}
      {!showSolutionsStep && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Left Column: Vehicle Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
                >
                  <DirectionsCar sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    {t('newFileService.vehicleInformation.title')}
                  </Typography>
                </Box>

                {/*<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>*/}
                {/*  <Tabs*/}
                {/*    value={vehicleEntryMode}*/}
                {/*    onChange={handleVehicleEntryModeChange}*/}
                {/*    variant="fullWidth"*/}
                {/*  >*/}
                {/*    <Tab*/}
                {/*      label={t('newFileService.vehicleInformation.tabs.auto')}*/}
                {/*      value="list"*/}
                {/*    />*/}
                {/*    <Tab*/}
                {/*      label={t('newFileService.vehicleInformation.tabs.manual')}*/}
                {/*      value="manual"*/}
                {/*    />*/}
                {/*  </Tabs>*/}
                {/*</Box>*/}

                {vehicleEntryMode === 'list' ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {t('newFileService.vehicleInformation.fields.type')}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.type'
                          )}
                          value={formData.makeType}
                          onChange={event =>
                            setFormData(prev => ({
                              ...prev,
                              makeType: event.target.value as string,
                            }))
                          }
                          disabled={loadingVehicleTypes}
                        >
                          {loadingVehicleTypes ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} />
                            </MenuItem>
                          ) : (
                            vehicleTypes.map(type => (
                              <MenuItem value={type.id} key={type.id}>
                                {type.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Autocomplete
                        fullWidth
                        options={carBrands}
                        getOptionLabel={option => option.name}
                        loading={loadingCarBrands}
                        value={selectedCarBrand}
                        inputValue={carSearchInput}
                        onInputChange={(_event, newInputValue) => {
                          setCarSearchInput(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          setSelectedCarBrand(newValue);
                          setFormData(prev => ({
                            ...prev,
                            car: newValue ? newValue.id : '',
                          }));
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'newFileService.vehicleInformation.fields.brand'
                            )}
                            required
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingCarBrands ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        noOptionsText={t(
                          'newFileService.vehicleInformation.noOptions.noBrandsFound'
                        )}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Autocomplete
                        fullWidth
                        disabled={!selectedCarBrand}
                        options={carModels}
                        getOptionLabel={option => option.name}
                        loading={loadingCarModels}
                        value={selectedCarModel}
                        inputValue={modelSearchInput}
                        onInputChange={(_event, newInputValue) => {
                          setModelSearchInput(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          setSelectedCarModel(newValue);
                          setFormData(prev => ({
                            ...prev,
                            model: newValue ? newValue.id : '',
                          }));
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'newFileService.vehicleInformation.fields.model'
                            )}
                            placeholder={
                              !selectedCarBrand
                                ? t(
                                    'newFileService.vehicleInformation.placeholders.selectBrandFirst'
                                  )
                                : t(
                                    'newFileService.vehicleInformation.placeholders.searchModel'
                                  )
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingCarModels ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        noOptionsText={t(
                          'newFileService.vehicleInformation.noOptions.noModelsFound'
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        sx={{ width: '100%' }}
                        label={t(
                          'newFileService.vehicleInformation.fields.vinCode'
                        )}
                        value={formData.vinCode}
                        onChange={event =>
                          setFormData(prev => ({
                            ...prev,
                            vinCode: event.target.value,
                          }))
                        }
                        name="vinCode"
                        autoComplete="on"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        fullWidth
                        disabled={!selectedCarModel}
                        options={availableYears}
                        getOptionLabel={option => option}
                        value={selectedYear}
                        inputValue={yearSearchInput}
                        onInputChange={(_event, newInputValue) => {
                          setYearSearchInput(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          setSelectedYear(newValue);
                          setFormData(prev => ({
                            ...prev,
                            year: newValue || '',
                          }));
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'newFileService.vehicleInformation.fields.year'
                            )}
                            placeholder={
                              !selectedCarModel
                                ? t(
                                    'newFileService.vehicleInformation.placeholders.selectModelFirst'
                                  )
                                : t(
                                    'newFileService.vehicleInformation.placeholders.selectYear'
                                  )
                            }
                            InputProps={{
                              ...params.InputProps,
                            }}
                          />
                        )}
                        noOptionsText={t(
                          'newFileService.vehicleInformation.noOptions.noYearsAvailable'
                        )}
                        filterOptions={(options, params) => {
                          const filtered = options.filter(option =>
                            option
                              .toLowerCase()
                              .includes(params.inputValue.toLowerCase())
                          );
                          return filtered;
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12 }}>
                      <Autocomplete
                        fullWidth
                        disabled={!selectedCarModel || !selectedYear}
                        options={availableEngines}
                        getOptionLabel={option =>
                          `${option.name} (${option.gearbox}, ${option.fuel})`
                        }
                        value={selectedEngine}
                        inputValue={engineSearchInput}
                        onInputChange={(_event, newInputValue) => {
                          setEngineSearchInput(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          setSelectedEngine(newValue);
                          setFormData(prev => ({
                            ...prev,
                            engine: newValue ? newValue.id : '',
                          }));
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'newFileService.vehicleInformation.fields.engine'
                            )}
                            placeholder={
                              !selectedCarModel || !selectedYear
                                ? t(
                                    'newFileService.vehicleInformation.placeholders.selectModelAndYearFirst'
                                  )
                                : t(
                                    'newFileService.vehicleInformation.placeholders.selectEngine'
                                  )
                            }
                            InputProps={{
                              ...params.InputProps,
                            }}
                          />
                        )}
                        noOptionsText={t(
                          'newFileService.vehicleInformation.noOptions.noEnginesAvailable'
                        )}
                        filterOptions={(options, params) => {
                          const filtered = options.filter(
                            option =>
                              option.name
                                .toLowerCase()
                                .includes(params.inputValue.toLowerCase()) ||
                              option.gearbox
                                .toLowerCase()
                                .includes(params.inputValue.toLowerCase()) ||
                              option.fuel
                                .toLowerCase()
                                .includes(params.inputValue.toLowerCase())
                          );
                          return filtered;
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        fullWidth
                        disabled={!selectedEngine}
                        options={ecuBrands}
                        getOptionLabel={option => option.name ?? ''}
                        loading={loadingEcuBrands}
                        value={selectedEcuBrand}
                        inputValue={ecuBrandSearchInput}
                        onInputChange={(_event, newInputValue) => {
                          setEcuBrandSearchInput(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          setSelectedEcuBrand(newValue);
                          setFormData(prev => ({
                            ...prev,
                            ecuBrand: newValue ? newValue.id : '',
                            ecuModel: '',
                          }));
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'newFileService.vehicleInformation.fields.ecuBrand'
                            )}
                            placeholder={
                              !selectedEngine
                                ? t(
                                    'newFileService.vehicleInformation.placeholders.selectEngineFirst'
                                  )
                                : t(
                                    'newFileService.vehicleInformation.placeholders.searchEcuBrand'
                                  )
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingEcuBrands ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        noOptionsText={t(
                          'newFileService.vehicleInformation.noOptions.noEcuBrandsFound'
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        fullWidth
                        disabled={!selectedEcuBrand}
                        options={ecuModels}
                        getOptionLabel={option => option.name ?? ''}
                        loading={loadingEcuModels}
                        value={selectedEcuModel}
                        inputValue={ecuModelSearchInput}
                        onInputChange={(_event, newInputValue) => {
                          setEcuModelSearchInput(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          setSelectedEcuModel(newValue);
                          setFormData(prev => ({
                            ...prev,
                            ecuModel: newValue ? newValue.id : '',
                          }));
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'newFileService.vehicleInformation.fields.ecuModel'
                            )}
                            placeholder={
                              !selectedEcuBrand
                                ? t(
                                    'newFileService.vehicleInformation.placeholders.selectEcuBrandFirst'
                                  )
                                : t(
                                    'newFileService.vehicleInformation.placeholders.searchEcuModel'
                                  )
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingEcuModels ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        noOptionsText={t(
                          'newFileService.vehicleInformation.noOptions.noEcuModelsFound'
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth required>
                        <InputLabel>
                          {t(
                            'newFileService.vehicleInformation.fields.readingMode'
                          )}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.readingMode'
                          )}
                          value={readingMode}
                          onChange={e => setReadingMode(e.target.value)}
                          name="readingMode"
                        >
                          {READING_MODE_OPTIONS.map(mode => (
                            <MenuItem key={mode} value={mode}>
                              {mode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {vehicleSummaryCard && (
                      <Grid size={{ xs: 12 }}>{vehicleSummaryCard}</Grid>
                    )}
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {t('newFileService.vehicleInformation.fields.type')}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.type'
                          )}
                          value={formData.makeType}
                          onChange={event =>
                            setFormData(prev => ({
                              ...prev,
                              makeType: event.target.value as string,
                            }))
                          }
                          disabled={loadingVehicleTypes}
                        >
                          {loadingVehicleTypes ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} />
                            </MenuItem>
                          ) : (
                            vehicleTypes.map(type => (
                              <MenuItem value={type.id} key={type.id}>
                                {type.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Autocomplete
                        fullWidth
                        options={carBrands}
                        getOptionLabel={option => option.name}
                        loading={loadingCarBrands}
                        value={selectedManualCarBrand}
                        inputValue={manualCarBrandInput}
                        onInputChange={(_event, newInputValue) => {
                          setManualCarBrandInput(newInputValue);
                          setCarSearchInput(newInputValue);
                        }}
                        onChange={(_event, newValue) => {
                          setSelectedManualCarBrand(newValue);
                          setManualVehicleData(prev => ({
                            ...prev,
                            brand: newValue ? newValue.id : '',
                          }));
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'newFileService.vehicleInformation.fields.brand'
                            )}
                            name="manualBrand"
                            required
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingCarBrands ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        noOptionsText={t(
                          'newFileService.vehicleInformation.noOptions.noBrandsFound'
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t(
                          'newFileService.vehicleInformation.fields.model'
                        )}
                        value={manualVehicleData.model}
                        onChange={handleManualVehicleChange('model')}
                        name="manualModel"
                        autoComplete="on"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth required>
                        <InputLabel>
                          {t('newFileService.vehicleInformation.fields.year')}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.year'
                          )}
                          value={manualVehicleData.year}
                          onChange={e =>
                            setManualVehicleData(prev => ({
                              ...prev,
                              year: e.target.value,
                            }))
                          }
                          name="manualYear"
                        >
                          {YEAR_OPTIONS.map(year => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t(
                          'newFileService.vehicleInformation.fields.vinCode'
                        )}
                        value={manualVehicleData.vinCode}
                        onChange={handleManualVehicleChange('vinCode')}
                        name="vinCode"
                        autoComplete="on"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t(
                          'newFileService.vehicleInformation.fields.engine'
                        )}
                        value={manualVehicleData.engine}
                        onChange={handleManualVehicleChange('engine')}
                        name="manualEngine"
                        autoComplete="on"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {t(
                            'newFileService.vehicleInformation.fields.gearbox'
                          )}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.gearbox'
                          )}
                          value={manualVehicleData.gearbox}
                          onChange={e =>
                            handleManualVehicleChange('gearbox')({
                              target: { value: e.target.value },
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                        >
                          <MenuItem value="">
                            <em>
                              {t(
                                'newFileService.vehicleInformation.manualOptions.select'
                              )}
                            </em>
                          </MenuItem>
                          <MenuItem value="Manual">
                            {t(
                              'newFileService.vehicleInformation.manualOptions.gearbox.manual'
                            )}
                          </MenuItem>
                          <MenuItem value="Automatic">
                            {t(
                              'newFileService.vehicleInformation.manualOptions.gearbox.automatic'
                            )}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>
                          {t('newFileService.vehicleInformation.fields.fuel')}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.fuel'
                          )}
                          value={manualVehicleData.fuel}
                          onChange={e =>
                            handleManualVehicleChange('fuel')({
                              target: { value: e.target.value },
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                        >
                          <MenuItem value="">
                            <em>
                              {t(
                                'newFileService.vehicleInformation.manualOptions.select'
                              )}
                            </em>
                          </MenuItem>
                          <MenuItem value="Petrol">
                            {t(
                              'newFileService.vehicleInformation.manualOptions.fuel.petrol'
                            )}
                          </MenuItem>
                          <MenuItem value="Diesel">
                            {t(
                              'newFileService.vehicleInformation.manualOptions.fuel.diesel'
                            )}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t(
                          'newFileService.vehicleInformation.fields.carPower'
                        )}
                        placeholder={t(
                          'newFileService.vehicleInformation.placeholders.carPowerPlaceholder'
                        )}
                        value={manualVehicleData.power}
                        onChange={handleManualVehicleChange('power')}
                        name="manualPower"
                        autoComplete="on"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth required>
                        <InputLabel>
                          {t(
                            'newFileService.vehicleInformation.fields.ecuBrand'
                          )}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.ecuBrand'
                          )}
                          value={manualVehicleData.ecuBrand}
                          onChange={e =>
                            setManualVehicleData(prev => ({
                              ...prev,
                              ecuBrand: e.target.value,
                            }))
                          }
                          name="manualEcuBrand"
                        >
                          {ECU_BRAND_OPTIONS.map(brand => (
                            <MenuItem key={brand} value={brand}>
                              {brand}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label={t(
                          'newFileService.vehicleInformation.fields.ecuModel'
                        )}
                        value={manualVehicleData.ecuModel}
                        onChange={handleManualVehicleChange('ecuModel')}
                        name="manualEcuModel"
                        autoComplete="on"
                        placeholder="MD1CP001"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth required>
                        <InputLabel>
                          {t(
                            'newFileService.vehicleInformation.fields.readingMode'
                          )}
                        </InputLabel>
                        <Select
                          label={t(
                            'newFileService.vehicleInformation.fields.readingMode'
                          )}
                          value={readingMode}
                          onChange={e => setReadingMode(e.target.value)}
                          name="readingMode"
                        >
                          {READING_MODE_OPTIONS.map(mode => (
                            <MenuItem key={mode} value={mode}>
                              {mode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {vehicleSummaryCard && (
                      <Grid size={{ xs: 12 }}>{vehicleSummaryCard}</Grid>
                    )}
                  </Grid>
                )}
              </Grid>

              {/* Right Column: File Upload & Tools */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
                >
                  <Folder sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                    {t('newFileService.fileUpload.title')}
                  </Typography>
                  <Tooltip
                    title={t('newFileService.fileUpload.supportedFilesTooltip')}
                  >
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* File Upload Zone */}
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive
                      ? 'action.hover'
                      : 'background.default',
                    transition: 'all 0.3s ease',
                    mb: 3,
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUpload
                    sx={{ fontSize: 48, color: 'primary.main', mb: 2 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {t('newFileService.fileUpload.dragAndDrop')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('newFileService.fileUpload.clickToBrowse')}
                  </Typography>
                </Box>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('newFileService.fileUpload.uploadedFiles')}
                    </Typography>
                    {uploadedFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={`${file.name} (${(file.size / 1024).toFixed(2)} KB)`}
                        onDelete={() => handleFileRemove(index)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}

              </Grid>
            </Grid>

            {formError && (
              <Alert variant="filled" severity="error" sx={{ mt: 4 }}>
                {formError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={handleProceedToSolutions}
                sx={{ px: 4, py: 1.5 }}
              >
                {t('newFileService.buttons.checkSolutions')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {showSolutionsStep && (
        <Card sx={{ mt: 3, overflow: 'visible' }}>
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {t('newFileService.solutions.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('newFileService.solutions.description')}
            </Typography>

            {vehicleSummaryCard && (
              <Box sx={{ mb: 3 }}>{vehicleSummaryCard}</Box>
            )}

            <Box
              sx={theme => ({
                position: 'sticky',
                top: config.topbarHeight,
                zIndex: 10,
                py: 1,
                mb: 1,
                backgroundColor: theme.palette.background.paper,
              })}
            >
              <Typography
                variant="body1"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                {t('newFileService.solutions.selected')}{' '}
                {selectedSolutions.length}{' '}
                {selectedSolutions.length === 1
                  ? t('newFileService.solutions.solution')
                  : t('newFileService.solutions.solutions')}
                <>
                  {' '}
                  · {t('newFileService.solutions.totalAmount')}{' '}
                  {calculatingPrice ? (
                    <CircularProgress
                      size={16}
                      sx={{ ml: 1, verticalAlign: 'middle' }}
                    />
                  ) : (
                    <>
                      {totalPrice} {t('newFileService.solutions.credits')}
                      <ShoppingCartOutlined
                        sx={{ fontSize: 18, ml: 0.5, mb: '1px', verticalAlign: 'text-bottom' }}
                      />
                    </>
                  )}
                </>
              </Typography>
            </Box>

            {formError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {formError}
              </Alert>
            )}

            <Divider sx={{ mb: 3 }} />

            {loadingSolutions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : solutionCatalog.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('newFileService.solutions.noSolutionsAvailable')}
              </Alert>
            ) : (
              solutionCatalog.map((section, index) => (
                <Box key={section.title || `section-${index}`} sx={{ mb: 4 }}>
                  {section.title && section.title.trim() && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 1.5,
                      }}
                    >
                      {section.title}
                    </Typography>
                  )}
                  <Grid container spacing={2}>
                    {section.items.map(item => (
                      <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: theme =>
                              theme.palette.background.paper,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: theme => theme.shadows[4],
                            },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Checkbox
                              checked={selectedSolutions.includes(item.id)}
                              onChange={handleSolutionToggle(item.id)}
                              color="primary"
                              disabled={
                                item.level === 2 &&
                                item.isFreeWhenStageSelected &&
                                !hasLevel1Selected
                              }
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight={600}>
                                {item.name}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))
            )}

            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {t('newFileService.description.title')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t('newFileService.description.placeholder')}
                variant="outlined"
                name="fileServiceDescription"
                autoComplete="on"
              />
            </Box>

            <Divider sx={{ my: 4 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={
                  creatingFileService ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CheckCircle />
                  )
                }
                onClick={handleCreateFileService}
                disabled={creatingFileService}
                sx={{ px: 4, py: 1.5 }}
              >
                {creatingFileService
                  ? t('newFileService.solutions.creating')
                  : t('newFileService.solutions.createFileService')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewFileServicePage;
